import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ChangelogApi from '../../../api/changelogApi.js';

/**
 * Custom hook for fetching and managing changelog state.
 * Does NOT depend on BrowserRouter or useLocation.
 *
 * @param {Object} options
 * @param {string} [options.area] - Area filter (e.g. 'resources', 'calendar')
 * @param {boolean} [options.skip] - Skip fetching
 */
export function useChangelog({ area, skip = false } = {}) {
	const [releases, setReleases] = useState([]);
	const [lastSeenReleaseId, setLastSeenReleaseId] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const fetchedRef = useRef(false);
	const prevAreaRef = useRef(area);

	// Reset fetch guard when area changes so new area triggers refetch
	if (area !== prevAreaRef.current) {
		prevAreaRef.current = area;
		fetchedRef.current = false;
	}

	const fetchChangelog = useCallback(async () => {
		if (skip || fetchedRef.current) return;
		fetchedRef.current = true;
		setLoading(true);
		setError(null);
		try {
			const { data } = await ChangelogApi.getChangelog(area);
			const responseData = data?.data ?? data;
			setReleases(responseData?.releases ?? []);
			setLastSeenReleaseId(responseData?.lastSeenReleaseId ?? null);
		} catch (err) {
			setError(err.message ?? 'Failed to load changelog');
			fetchedRef.current = false;
		} finally {
			setLoading(false);
		}
	}, [area]);

	useEffect(() => {
		fetchChangelog();
	}, [fetchChangelog]);

	const markAsSeen = useCallback(async () => {
		try {
			const latestRelease = releases[0];
			if (!latestRelease) return;
			await ChangelogApi.markAsSeen(latestRelease.id, area);
			setLastSeenReleaseId(latestRelease.id);
		} catch (err) {
			console.error('Failed to mark changelog as seen:', err);
		}
	}, [releases, area]);

	// Backend `ChangelogManager::seenCoversRelease()` decides coverage by
	// `releaseDate` first (release id only as a same-date tie-breaker), and the
	// public list arrives date-sorted DESC (newest first). So newness must be
	// derived from POSITION in this ordered list relative to the last-seen
	// release — NOT a raw `id >` comparison, which wrongly keeps a
	// later-created-but-older-dated release flagged "New".
	// Index of the last-seen release within the current ordered list; -1 when
	// nothing is seen yet OR the marker isn't in the list (→ treat all as New).
	// TODO: if the backend later exposes `lastSeenReleaseDate`, prefer direct
	// (releaseDate, id) comparison over this index lookup.
	const lastSeenIndex = useMemo(() => {
		if (lastSeenReleaseId == null) return -1;
		return releases.findIndex(release => release.id === lastSeenReleaseId);
	}, [releases, lastSeenReleaseId]);

	const isReleaseNew = useCallback(
		release => {
			if (lastSeenIndex === -1) return true;
			const index = releases.findIndex(r => r.id === release.id);
			return index !== -1 && index < lastSeenIndex;
		},
		[releases, lastSeenIndex]
	);

	const newItemsCount = useMemo(
		() =>
			releases.reduce((count, release, index) => {
				const isNew = lastSeenIndex === -1 || index < lastSeenIndex;
				return isNew ? count + (release.items?.length ?? 0) : count;
			}, 0),
		[releases, lastSeenIndex]
	);

	return {
		releases,
		loading,
		error,
		markAsSeen,
		isReleaseNew,
		newItemsCount,
		refetch: () => {
			fetchedRef.current = false;
			fetchChangelog();
		}
	};
}
